import { AnimationControlsContext } from '@shared/contexts/AnimationControlsContext';
import { AnimationControls } from '@shared/models/animationControls';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

interface AnimationControlsProviderProps {
  children: ReactNode;
}

function pauseIfRunning(animation: Animation) {
  if (animation.playState === 'running') {
    animation.pause();
  }
}

export default function AnimationControlsProvider({
  children,
}: AnimationControlsProviderProps) {
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle('animations-paused', isPaused);
    return () => {
      document.documentElement.classList.remove('animations-paused');
    };
  }, [isPaused]);

  useEffect(() => {
    if (!isPaused) {
      return;
    }

    document.getAnimations().forEach(pauseIfRunning);

    // Pause CSS animations and transitions that start while paused.
    function onAnimationStart(event: Event) {
      if (event.target instanceof Element) {
        event.target.getAnimations().forEach(pauseIfRunning);
      }
    }

    document.addEventListener('animationstart', onAnimationStart, true);
    document.addEventListener('transitionrun', onAnimationStart, true);

    return () => {
      document.removeEventListener('animationstart', onAnimationStart, true);
      document.removeEventListener('transitionrun', onAnimationStart, true);
      document.getAnimations().forEach((animation) => {
        if (animation.playState === 'paused') {
          animation.play();
        }
      });
    };
  }, [isPaused]);

  const toggle = useCallback(() => setIsPaused((paused) => !paused), []);

  const animationControls = useMemo<AnimationControls>(
    () => ({ isPaused, toggle }),
    [isPaused, toggle],
  );

  return (
    <AnimationControlsContext.Provider value={animationControls}>
      {children}
    </AnimationControlsContext.Provider>
  );
}
